import { NestedTreeControl } from '@angular/cdk/tree';

import {
  Component,
  Input,
  OnInit,
  OnChanges,
  ViewEncapsulation,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { TranslateService } from '@ngx-translate/core';
import { filter, finalize, forkJoin, iif, takeUntil, tap, Subscription } from 'rxjs';
import { CategoriesCompliance } from '../models/categories-compliance.model';
import { EvaluatedSummaryType, ExecuteVisitType } from '../models/execute-visit.model';
import { MemberModel } from '../models/member.model';
import { RolesEnum, VisitMetaData } from '../models/visit-metadata.model';
// import { CriterionsService } from '../services/criterions.service';
import _cloneDeep from 'lodash/cloneDeep';
import { BaseComponent } from '../base.component';
import { ICriterion } from '../../distribute-team-members/models/criterion';
import { CriterionsService } from '../services/criterions.service';
import { MasarCriterionsService } from '../services/masar-criterions.service';
import { showToast } from '../../toast.helper';
import { ToastrService } from 'ngx-toastr';
import { Summary } from '../models/summary.model';

const MasarExecutiveSummaries = 'executive-summaries';
export const QUILL_MODULES_LITE = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ direction: 'rtl' }],
    [{ align: [] }],
    ['clean'],
  ],
};

@Component({
  selector: 'app-masar-criterions-tree',
  templateUrl: './masar-criterions-tree.component.html',
  styleUrls: ['./masar-criterions-tree.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MasarCriterionsTreeComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() viewOnly: boolean;
  @Input() categoriesCompliance: CategoriesCompliance[];
  @Input() visitMetaData: VisitMetaData;
  @Input() sessionId: number | string;
  @Input() executeVisitType: ExecuteVisitType;
  @Input() evaluatedSummaryType: EvaluatedSummaryType;
  @Input() responseNotesDecision: boolean;
  @Input() mistakeDisabled: boolean;
  @Input() disableEntityResponse: boolean;
  @Input() disableAttachments: boolean;
  @Input() hideMistake: boolean;
  decisionInputs$ = this.criterionsService.decisionInputs$;
  quillModules = QUILL_MODULES_LITE;
  criterions: ICriterion[] = [];
  summaries = [
    {
      type: 'recommendations-summaries',
      filter: 'summaryOfRecommendations',
      label: 'forms.common.Criteria.summaryOfRecommendations'
    },
    {
      type: 'commendations-summaries',
      filter: 'summaryOfCommendations',
      label: 'forms.common.Criteria.summaryOfCommendations'
    },
    {
      type: 'suggestions-summaries',
      filter: 'summaryOfSuggestions',
      label: 'forms.common.Criteria.summaryOfSuggestions'
    }
  ];
  allCriterionEvaluated = false;
  executiveSummaryHTML = '';
  executiveSummary = '';
  notesDecision: boolean;
  subscription: Subscription;

  get isMistakeDisabled() {
    const decisionInput = this.criterionsService.decisionInputs$.value;
    return decisionInput?.viewOnly || this.mistakeDisabled ||
      decisionInput.executeVisitType == ExecuteVisitType.FinalReport ||
      decisionInput.executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries;
  }
  get showExecutivePlan() {
    return this.executeVisitType == ExecuteVisitType.InstitutionEntries ||
      this.executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries;
  }
  get enabledExecutiveSummary() {
    const decisionInput = this.criterionsService.decisionInputs$.value;
    return this.masarCriterionsService.allCriterionEvaluated$.value &&
      decisionInput.enableEdit &&
      decisionInput.executeVisitType == ExecuteVisitType.FinalReport;
  }

  selectedNode: ICriterion = null;
  treeControl = new NestedTreeControl<ICriterion>((node) => node.subCriteria);
  dataSource: MatTreeNestedDataSource<ICriterion>;

  isLoadingResults = false;

  showMemberDropdown: boolean;
  selectedMember: MemberModel;
  mainCriterionKeys: string[];

  readonly hasChild = (_: number, node: ICriterion) =>
    !!node.subCriteria && node.subCriteria.length > 0;

  readonly trackBy = (_: number, node: ICriterion) => node.id;

  constructor(
    private criterionsService: CriterionsService,
    private masarCriterionsService: MasarCriterionsService,
    public translateService: TranslateService,
    public toastrService: ToastrService,
  ) {
    super();
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges) {
    this.showMemberDropdown = this.executeVisitType == ExecuteVisitType.SessionDetails && this.visitMetaData.role == RolesEnum.TEAM_LEADER;
    if (!this.showMemberDropdown) this.getTreeData();

    this.decisionTakenListener();
    this.hasMistakeListener();
    this.criterionKeyListener();
    this.registerCopyToExecutiveSummary();
    this.notesDecision = this.responseNotesDecision;
  }

  onExecutiveSummaryChange(event: any) {
    this.executiveSummary = event.htmlValue || '';
  }

  private decisionTakenListener() {
    this.criterionsService.decisionTaken$
      .pipe(
        filter(_ => !!this.selectedNode),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.selectedNode.evaluate = true;
        // this.updateResultData();
        this.getTreeData();
      });
  }
  private hasMistakeListener() {
    this.criterionsService.hasMistake$
      .pipe(
        filter(_ => !!this.selectedNode),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => this.selectedNode.hasError = value);
  }
  private criterionKeyListener() {
    this.masarCriterionsService.updatedCriterionKey$
      .pipe(
        filter(data => !!data && (!this.showMemberDropdown || (this.showMemberDropdown && !!this.selectedMember))),
        takeUntil(this.destroy$)
      )
      .subscribe(_ => {
        this.getTreeData();
      });
  }

  memberChanged(member: MemberModel) {
    this.selectedNode = null;
    this.getTreeData(member);
  }
  rowClicked(node: ICriterion) {
    if (this.selectedNode?.id !== node?.id) {
      this.selectedNode = node;
      this.criterionsService.criterion$.next(node);
    }
  }
  getTreeData(member: MemberModel = null) {
    const reviewerId = member?.reviewer?.id;
    const enableDecisionEdit = !reviewerId || reviewerId == this.visitMetaData.reviewerId;

    this.criterionsService.decisionInputs$.next({
      categoriesCompliance: this.categoriesCompliance,
      visitMetaData: this.visitMetaData,
      isMasar: true,
      enableEdit: !this.viewOnly && enableDecisionEdit,
      currentMemberId: member?.id || this.visitMetaData.memberId,
      currentReviewerId: member?.reviewer?.id || this.visitMetaData.reviewerId,
      viewOnly: this.viewOnly,
      executeVisitType: this.executeVisitType,
      evaluatedSummaryType: this.evaluatedSummaryType,
      disableEntityResponse: this.disableEntityResponse,
      disableAttachments: this.disableAttachments,
    });

    this.isLoadingResults = true;
    forkJoin([
      iif(
        () => this.executeVisitType == ExecuteVisitType.SessionDetails,
        this.masarCriterionsService.getMyCriterionByVisitKey(this.visitMetaData.visitKey, reviewerId || this.visitMetaData.reviewerId),
        this.masarCriterionsService.getAllCriterionByVisitKey(this.visitMetaData.visitKey)
      ),
      this.criterionsService.getProccess(this.visitMetaData.accreditationKey, this.visitMetaData.eligibilityRequestId)
    ]).pipe(
      tap(() => this.getExecutiveSummary()),
      takeUntil(this.destroy$),
      finalize(() => {
        this.isLoadingResults = false;
      })
    ).subscribe((result) => {
      this.criterions = result[0];
      this.mainCriterionKeys = this.criterions.map(c => c.criterionKey);
      this.dataSource = new MatTreeNestedDataSource<ICriterion>();
      this.dataSource.data = this.criterions;
      this.updateResultData();
    });
  }

  getExecutiveSummary() {
    if (this.executeVisitType == ExecuteVisitType.SessionDetails) return;

    this.isLoadingResults = true;
    this.masarCriterionsService.getSummaries(
      this.visitMetaData.visitKey,
      MasarExecutiveSummaries
    ).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.isLoadingResults = false),
      filter(data => !!data?.length),
    ).subscribe(data => {
      this.executiveSummary = data[data.length - 1].summaryText;
      this.executiveSummaryHTML = data[data.length - 1].summaryText;
      this.updateResultData(this.executiveSummary);
    });
  }
  saveExecutiveSummary() {
    const summary: Summary = {
      noteOrder: 1,
      visitId: this.visitMetaData.visitId,
      visitKey: this.visitMetaData.visitKey,
      reviewerId: this.visitMetaData.reviewerId,
      summaryText: this.executiveSummary,
      deleted: false
    };
    this.isLoadingResults = true;
    this.masarCriterionsService.saveSummaries(MasarExecutiveSummaries, [summary])
      .pipe(finalize(() => this.isLoadingResults = false))
      .subscribe(data => {
        this.updateResultData(this.executiveSummary);
        showToast(this.toastrService,
          this.translateService.instant("forms.common.Criteria.Saved done"),
          this.translateService.instant("forms.common.Criteria.Saved Successfully"),
          false
        );
      });
  }

  updateResultData(executiveSummary: string = '') {
    const value = this.criterionsService.results$.getValue();
    value.executiveSummary = executiveSummary;
    value.responseNotesDecision = this.notesDecision;
    value.criterion = this.dataSource.data;
    value.flatCriterion = _cloneDeep(value.criterion);

    let flatten = (children, extractChildren) => Array.prototype.concat.apply(
      children,
      children.map(x => flatten(extractChildren(x) || [], extractChildren))
    );
    let extractChildren = x => x.subCriteria;
    value.flatCriterion = flatten(extractChildren({ subCriteria: value.flatCriterion }), extractChildren)
      .map(x => delete x.subCriteria && x);

    this.allCriterionEvaluated = value.flatCriterion
      .every(c => c.evaluate);

    this.masarCriterionsService.allCriterionEvaluated$.next(this.allCriterionEvaluated);
    this.criterionsService.results$.next(value);
  }
  private registerCopyToExecutiveSummary() {
    this.subscription?.unsubscribe();
    this.subscription = this.criterionsService.sendToExecutiveSummary$
      .pipe(
        filter(data => !!data),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        this.executiveSummary += `${data.value}<br/>`;
        if (this.executiveSummaryHTML == null) this.executiveSummaryHTML = '';
        this.executiveSummaryHTML += `${data.value}<br/>`;
      });

  }
}
